import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney, inputPaise, moneyInputError, paiseToDecimal, parsePaise } from './allocation';
import styles from './Payments.module.css';
import { Button } from '../../components/ui/Button';
import { DialogHelp } from '../../help/DialogHelp';
export interface ChargeApprovalAdjustments {
  partnerAmount: string;
  shipperAmount: string;
  adjustmentNote?: string;
}
export interface PartnerChargeReview {
  id: number;
  requestedPartnerAmount: string;
  partnerAmount: string;
  shipperAmount: string;
  requestedByName: string | null;
  description?: string | null;
  proofUrl?: string | null;
}
export function ReviewPartnerChargeDialog({
  charge,
  isManager,
  invoiceIssued = false,
  onClose,
  onApprove,
  proofFeedback,
}: {
  proofFeedback?: ReactNode;
  charge: PartnerChargeReview;
  isManager: boolean;
  invoiceIssued?: boolean;
  onClose: () => void;
  onApprove: (request: ChargeApprovalAdjustments) => Promise<unknown> | void;
}) {
  const { t } = useTranslation();
  const [partner, setPartner] = useState(charge.partnerAmount);
  const [shipper, setShipper] = useState(charge.shipperAmount);
  const [note, setNote] = useState('');
  const [pending, setPending] = useState(false);
  const busy = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const changed =
    inputPaise(partner) !== parsePaise(charge.partnerAmount) ||
    inputPaise(shipper) !== parsePaise(charge.shipperAmount);
  const moneyError = (value: string) =>
    moneyInputError(value) ||
    (inputPaise(value) % 100n !== 0n ? 'Enter a whole-rupee amount.' : null);
  const invalid =
    moneyError(partner) || moneyError(shipper) || inputPaise(partner) + inputPaise(shipper) <= 0n;
  const invoiceBlocked = invoiceIssued && inputPaise(shipper) > 0n;
  const valid =
    !invalid &&
    !invoiceBlocked &&
    (!changed || (note.trim().length >= 3 && note.trim().length <= 500));
  const submit = async () => {
    if (!isManager || !valid || busy.current) return;
    busy.current = true;
    setPending(true);
    setError(null);
    try {
      await onApprove({
        partnerAmount: paiseToDecimal(inputPaise(partner)),
        shipperAmount: paiseToDecimal(inputPaise(shipper)),
        ...(changed ? { adjustmentNote: note.trim() } : {}),
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not approve this charge.');
    } finally {
      busy.current = false;
      setPending(false);
    }
  };
  return (
    <div className={styles.scrim}>
      <section
        role="dialog"
        aria-modal="true"
        aria-label={t('payments.reviewPartnerChargeDialog.title')}
        className={`${styles.dialog} ${styles.compact}`}
      >
        <header className={styles.header}>
          <h2>{t('payments.reviewPartnerChargeDialog.heading', { id: charge.id })}</h2>
          <DialogHelp context="review-charge" title={t('payments.reviewPartnerChargeDialog.title')} />
        </header>
        <div className={styles.body}>
          <p>
            {t('payments.reviewPartnerChargeDialog.requestedBy', {
              name: charge.requestedByName ?? t('payments.reviewPartnerChargeDialog.partnerFallback'),
              amount: formatMoney(parsePaise(charge.requestedPartnerAmount)),
            })}
          </p>
          {charge.description && <p>{charge.description}</p>}
          {proofFeedback}
          {charge.proofUrl && (
            <a href={charge.proofUrl} target="_blank" rel="noreferrer">
              {t('payments.reviewPartnerChargeDialog.viewAttachment')}
            </a>
          )}
          <label className={styles.field}>
            {t('payments.reviewPartnerChargeDialog.finalPartnerAmount')}
            <input
              aria-label={t('payments.reviewPartnerChargeDialog.finalPartnerAmount')}
              inputMode="decimal"
              value={partner}
              disabled={!isManager || pending}
              onChange={(e) => setPartner(e.target.value)}
            />
          </label>
          <label className={styles.field}>
            {t('payments.reviewPartnerChargeDialog.finalShipperAmount')}
            <input
              aria-label={t('payments.reviewPartnerChargeDialog.finalShipperAmount')}
              inputMode="decimal"
              value={shipper}
              disabled={!isManager || pending}
              onChange={(e) => setShipper(e.target.value)}
            />
          </label>
          {invoiceIssued && (
            <p role={invoiceBlocked ? 'alert' : undefined}>
              {t('payments.reviewPartnerChargeDialog.invoiceIssued')}
            </p>
          )}
          {isManager && (
            <label className={styles.field}>
              {t('payments.reviewPartnerChargeDialog.adjustmentNoteField')}
              <textarea
                aria-label={t('payments.reviewPartnerChargeDialog.adjustmentNoteLabel')}
                maxLength={500}
                value={note}
                disabled={pending}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
          )}
          {invalid && (
            <p role="alert">{t('payments.reviewPartnerChargeDialog.invalidAmounts')}</p>
          )}
          {error && <p role="alert">{error}</p>}
          <div className={styles.actions}>
            <Button variant="secondary" size="sm" disabled={pending} onClick={onClose}>
              {t('common.actions.close')}
            </Button>
            {isManager && (
              <Button variant="primary" size="sm" disabled={!valid || pending} onClick={submit}>
                {pending ? 'Approving…' : 'Approve charge'}
              </Button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
